import { documentApi, documentsApi } from 'app/services/api';
import { normalize } from 'normalizr';

import { HTTP_RESPONSE_STATUS_CODES } from 'constants/http';
import { catchAsyncActionError } from 'utils/action';
import { checkCannotBeDeleted, getRecipientCopyIds } from 'utils/documents';
import { createNamedActions } from 'utils/namedActionTypesProxy';

import { showMessageAction } from 'store/global/actions';

import { getDocuments } from '../selectors';

import { documentSchema } from './document.action';

export const DOCUMENTS_ACTION_TYPE = createNamedActions('documents', {
  GET_DOCUMENTS_LIST_TYPE: 'GET_DOCUMENTS_LIST_TYPE',
  GET_DOCUMENTS_LIST_SILENT_TYPE: 'GET_DOCUMENTS_LIST_SILENT_TYPE',
  UPDATE_DOCUMENT_CONTENT: 'UPDATE_DOCUMENT_CONTENT',
  UPDATE_DOCUMENT: 'UPDATE_DOCUMENT',
  RESTORE_DOCUMENT: 'RESTORE_DOCUMENT',
  DELETE_DOCUMENT: 'DELETE_DOCUMENT',
  UPLOAD_DOCUMENT: 'UPLOAD_DOCUMENT',
  GET_DOCUMENT_BY_ID: 'GET_DOCUMENT_BY_ID',
  GET_AI_EXTRACTION_RESULT: 'GET_AI_EXTRACTION_RESULT',
  TOGGLE_DOCUMENT_SELECTION: 'TOGGLE_DOCUMENT_SELECTION',
  RESET_SELECTED_DOCUMENTS: 'RESET_SELECTED_DOCUMENTS',
  COPY_DOCUMENTS_TO_TARGET_LOAN: 'COPY_DOCUMENTS_TO_TARGET_LOAN',
});

export const getDocumentsExecAction = params => async dispatch => {
  const { data } = await documentApi.getDocumentsV2(params);

  return dispatch({
    type: DOCUMENTS_ACTION_TYPE.GET_DOCUMENTS_LIST_TYPE,
    payload: normalize(data, [documentSchema]).entities.data,
  });
};

export const getDocumentsAction = catchAsyncActionError(getDocumentsExecAction);

// Background refresh (e.g. the milestone popover's hover fetch): updates the list
// without wiping the Documents tab's bulk selection the way the primary load does.
export const getDocumentsSilentAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await documentApi.getDocumentsV2(params);

  return dispatch({
    type: DOCUMENTS_ACTION_TYPE.GET_DOCUMENTS_LIST_SILENT_TYPE,
    payload: normalize(data, [documentSchema]).entities.data,
  });
});

export const updateDocumentContentAction = data => dispatch => {
  return dispatch({
    type: DOCUMENTS_ACTION_TYPE.UPDATE_DOCUMENT_CONTENT,
    payload: normalize(data, [documentSchema]).entities.data[data[0].id],
  });
};

const updateDocumentAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await documentsApi.editDocument(params);

  return dispatch({
    type: DOCUMENTS_ACTION_TYPE.UPDATE_DOCUMENT,
    payload: normalize(data, documentSchema).entities.data[data.id],
  });
});

// UPDATE_DOCUMENT replaces the store entry with the Skynet.Documents response, which lacks the
// response-only flags SkynetAPI adds (RecipientCopyOf, CannotBeDeleted); the silent reload brings
// them back, so it runs only when the moved document carried CannotBeDeleted or had copies to move.
export const moveDocumentToTypeAction = catchAsyncActionError(
  ({ documentId, documentTypeId, loanParams }) =>
    async (dispatch, getState) => {
      const documents = getDocuments(getState());
      const recipientCopyIds = getRecipientCopyIds(documents, documentId);
      const cannotBeDeleted = checkCannotBeDeleted(documents[documentId]);

      await dispatch(updateDocumentAction({ id: documentId, documentTypeId, silent: true }));

      if (!recipientCopyIds.length && !cannotBeDeleted) {
        return;
      }

      // allSettled, not all: a copy PUT that resolves after the reload would strip its flags again.
      const copyMoveResults = await Promise.allSettled(
        recipientCopyIds.map(id =>
          dispatch(updateDocumentAction({ id, documentTypeId, silent: true })),
        ),
      );

      await dispatch(getDocumentsSilentAction({ ...loanParams, silent: true }));

      const failedCopyMove = copyMoveResults.find(({ status }) => status === 'rejected');

      if (failedCopyMove) {
        throw failedCopyMove.reason;
      }
    },
);

export const restoreDocumentAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await documentsApi.restoreDocument(params);

  return dispatch({
    type: DOCUMENTS_ACTION_TYPE.RESTORE_DOCUMENT,
    payload: normalize(data, documentSchema).entities.data[data.id],
  });
});

export const deleteDocumentAction = catchAsyncActionError(params => async dispatch => {
  await documentApi.deleteDocument(params);

  dispatch(showMessageAction({ message: 'Document removed', variant: 'success' }));

  return dispatch({
    type: DOCUMENTS_ACTION_TYPE.DELETE_DOCUMENT,
    payload: params,
  });
});

export const getDocumentByIdAction = catchAsyncActionError(params => async dispatch => {
  const { data } = await documentsApi.getDocumentById(params);

  return dispatch({
    type: DOCUMENTS_ACTION_TYPE.GET_DOCUMENT_BY_ID,
    payload: normalize(data, documentSchema).entities.data[data.id],
  });
});

export const uploadDocumentAction = catchAsyncActionError(params => async dispatch => {
  await documentsApi.uploadDocument(params);

  return dispatch({
    type: DOCUMENTS_ACTION_TYPE.UPLOAD_DOCUMENT,
  });
});

export const createDocumentAction = catchAsyncActionError(params => async () => {
  const { data } = await documentApi.createDocumentV2(params);

  return data;
});

export const getAiExtractionResultAction = catchAsyncActionError(params => async dispatch => {
  try {
    const { data } = await documentsApi.getAiExtractionResult(params);

    return dispatch({
      type: DOCUMENTS_ACTION_TYPE.GET_AI_EXTRACTION_RESULT,
      payload: { aiAnalysisResult: data, id: params.documentId },
    });
  } catch (err) {
    // a document with no AI analysis result yet 404s - expected, not an error
    if (err.response?.status === HTTP_RESPONSE_STATUS_CODES.NOT_FOUND) {
      return dispatch({
        type: DOCUMENTS_ACTION_TYPE.GET_AI_EXTRACTION_RESULT,
        payload: { aiAnalysisResult: null, id: params.documentId },
      });
    }

    throw err;
  }
});

export const copyDocumentsToTargetLoanAction = catchAsyncActionError(params => async dispatch => {
  await documentApi.copyDocumentToTargetLoan(params);

  return dispatch({
    type: DOCUMENTS_ACTION_TYPE.COPY_DOCUMENTS_TO_TARGET_LOAN,
  });
});

export const toggleDocumentSelectionAction = documentId => ({
  type: DOCUMENTS_ACTION_TYPE.TOGGLE_DOCUMENT_SELECTION,
  payload: documentId,
});

export const resetSelectedDocumentsAction = () => ({
  type: DOCUMENTS_ACTION_TYPE.RESET_SELECTED_DOCUMENTS,
});
